/* eslint-disable */
import {
  assertBoolean,
  assertNumber,
  attachSingleReferenceComponent,
  attributeSelectionComponent,
  childEntitySelectionComponent,
  composedOfComponent,
  dimensionIndicatorComponent,
  distanceDimensionComponent,
  materialSwatchComponent,
  memoEval,
  referenceComponent,
  sceneEntityObjectPropertiesComponent,
  sceneEntityObjectRemoteMaterialOverrideComponent,
  sceneEntityRemoteMaterialOverrideComponent,
  sceneEntityRemoteObjectComponent,
} from '@salsita-ai/platform-project-sdk/domain'

import { attachComponent } from '../implementation.js'
import { Apron, DistressLevels, EdgeStyles, Finishes, LeafTypes, ThicknessesTypes } from '../model/index.js'

export const topComponents = [
  attachSingleReferenceComponent(
    attachComponent.withComponent(referenceComponent, 'Top.$apron', true).withAlias('Top'),
    Apron,
    'apronId'
  ),

  attachComponent
    .withComponent(composedOfComponent, '$apron', true)
    .withAlias('Top')
    .attach(
      memoEval(
        self => [self.$apron?.id ?? null],
        id => ({ childEntityIds: id === null ? [] : [id] })
      )
    ),

  attachComponent
    .withComponent(childEntitySelectionComponent, 'aprons', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.apronId, self.$apronOptions],
        (apronId, $apronOptions) => ({
          value: apronId,
          options: $apronOptions,
        })
      )
    ),

  attachComponent
    .withComponent(distanceDimensionComponent, 'thickness', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.thickness, self.$minThickness, self.$maxThickness],
        (thickness, $minThickness, $maxThickness) => ({
          value: thickness,
          min: $minThickness,
          max: $maxThickness,
        })
      ),
      state => {
        assertNumber(state.value)
        return { thickness: state.value }
      }
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'top', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$objectName],
        ($fileUrl, $objectName) => ({
          fileUrl: $fileUrl,
          objectName: $objectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'leafLeft', true)
    .withAlias('Top')
    .withGradualFilter({ active: self => self.$leafLeftVisible })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$leafObjectName],
        ($fileUrl, $leafObjectName) => ({
          fileUrl: $fileUrl,
          objectName: $leafObjectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'leafRight', true)
    .withAlias('Top')
    .withGradualFilter({ active: self => self.$leafRightVisible })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$leafObjectName],
        ($fileUrl, $leafObjectName) => ({
          fileUrl: $fileUrl,
          objectName: $leafObjectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'leafCenter', true)
    .withAlias('Top')
    .withGradualFilter({ active: self => self.$leafCenterVisible })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$leafObjectName],
        ($fileUrl, $leafObjectName) => ({
          fileUrl: $fileUrl,
          objectName: $leafObjectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'leafCenter2', true)
    .withAlias('Top')
    .withGradualFilter({ active: self => self.$leafCenter2Visible })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$leafObjectName],
        ($fileUrl, $leafObjectName) => ({
          fileUrl: $fileUrl,
          objectName: $leafObjectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteObjectComponent, 'leafPerimeter', true)
    .withAlias('Top')
    .withGradualFilter({ active: self => self.$leafPerimeterVisible })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$fileUrl, self.$leafPerimeterObjectName],
        ($fileUrl, $leafPerimeterObjectName) => ({
          fileUrl: $fileUrl,
          objectName: $leafPerimeterObjectName,
          dimensionUnit: 'inch',
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectPropertiesComponent, 'leafLeft', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$offsetY,
            self.$leafLeftOffsetZ,
            self.$leafLeftRotationY,
            self.$leafLengthShapekeyValue,
            self.$leafLengthScaleZero,
            self.$leafLengthScaleOne,
            self.$leafWidthShapekeyValue,
            self.$leafWidthScaleZero,
            self.$leafWidthScaleOne,
            self.$leafThicknessShapekeyValue,
            self.$leafThicknessScaleOne,
            self.$leafThicknessScaleZero,
            self.$leafDupontEdgeOnSideValue,
            self.$leafWavyEdgeOnSideValue,
            self.$bevelThickness,
            self.$bevelScaleZero,
            self.$bevelScaleOne,
          ],
        (
          $offsetY,
          $leafLeftOffsetZ,
          $leafLeftRotationY,
          $leafLengthShapekeyValue,
          $leafLengthScaleZero,
          $leafLengthScaleOne,
          $leafWidthShapekeyValue,
          $leafWidthScaleZero,
          $leafWidthScaleOne,
          $leafThicknessShapekeyValue,
          $leafThicknessScaleOne,
          $leafThicknessScaleZero,
          $leafDupontEdgeOnSideValue,
          $leafWavyEdgeOnSideValue,
          $bevelThickness,
          $bevelScaleZero,
          $bevelScaleOne
        ) => ({
          offsetY: $offsetY,
          offsetZ: $leafLeftOffsetZ,
          rotationY: $leafLeftRotationY,
          morphTargetInfluences: {
            length: {
              value: $leafLengthShapekeyValue,
              scaleZero: $leafLengthScaleZero,
              scaleOne: $leafLengthScaleOne,
            },
            width: {
              value: $leafWidthShapekeyValue,
              scaleZero: $leafWidthScaleZero,
              scaleOne: $leafWidthScaleOne,
            },
            thickness: {
              value: $leafThicknessShapekeyValue,
              scaleOne: $leafThicknessScaleOne,
              scaleZero: $leafThicknessScaleZero,
            },
            ogeeEdgeOnSide: {
              value: $leafDupontEdgeOnSideValue,
              scaleZero: 0,
              scaleOne: 1,
            },
            wavyEdgeOnSide: {
              value: $leafWavyEdgeOnSideValue,
              scaleZero: 0,
              scaleOne: 1,
            },
            bevel: {
              value: $bevelThickness,
              scaleZero: $bevelScaleZero,
              scaleOne: $bevelScaleOne,
            },
          },
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectPropertiesComponent, 'leafRight', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$offsetY,
            self.$leafRightOffsetZ,
            self.$leafRightRotationY,
            self.$leafLengthShapekeyValue,
            self.$leafLengthScaleZero,
            self.$leafLengthScaleOne,
            self.$leafWidthShapekeyValue,
            self.$leafWidthScaleZero,
            self.$leafWidthScaleOne,
            self.$leafThicknessShapekeyValue,
            self.$leafThicknessScaleOne,
            self.$leafThicknessScaleZero,
            self.$leafDupontEdgeOnSideValue,
            self.$leafWavyEdgeOnSideValue,
            self.$bevelThickness,
            self.$bevelScaleZero,
            self.$bevelScaleOne,
          ],
        (
          $offsetY,
          $leafRightOffsetZ,
          $leafRightRotationY,
          $leafLengthShapekeyValue,
          $leafLengthScaleZero,
          $leafLengthScaleOne,
          $leafWidthShapekeyValue,
          $leafWidthScaleZero,
          $leafWidthScaleOne,
          $leafThicknessShapekeyValue,
          $leafThicknessScaleOne,
          $leafThicknessScaleZero,
          $leafDupontEdgeOnSideValue,
          $leafWavyEdgeOnSideValue,
          $bevelThickness,
          $bevelScaleZero,
          $bevelScaleOne
        ) => ({
          offsetY: $offsetY,
          offsetZ: $leafRightOffsetZ,
          rotationY: $leafRightRotationY,
          morphTargetInfluences: {
            length: {
              value: $leafLengthShapekeyValue,
              scaleZero: $leafLengthScaleZero,
              scaleOne: $leafLengthScaleOne,
            },
            width: {
              value: $leafWidthShapekeyValue,
              scaleZero: $leafWidthScaleZero,
              scaleOne: $leafWidthScaleOne,
            },
            thickness: {
              value: $leafThicknessShapekeyValue,
              scaleOne: $leafThicknessScaleOne,
              scaleZero: $leafThicknessScaleZero,
            },
            ogeeEdgeOnSide: {
              value: $leafDupontEdgeOnSideValue,
              scaleZero: 0,
              scaleOne: 1,
            },
            wavyEdgeOnSide: {
              value: $leafWavyEdgeOnSideValue,
              scaleZero: 0,
              scaleOne: 1,
            },
            bevel: {
              value: $bevelThickness,
              scaleZero: $bevelScaleZero,
              scaleOne: $bevelScaleOne,
            },
          },
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectPropertiesComponent, 'leafCenter', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafCenterOffsetY,
            self.$leafCenterOffsetZ,
            self.$leafCenterRotationY,
            self.$leafLengthShapekeyValue,
            self.$leafLengthScaleZero,
            self.$leafLengthScaleOne,
            self.$leafWidthShapekeyValue,
            self.$leafWidthScaleZero,
            self.$leafWidthScaleOne,
            self.$leafThicknessShapekeyValue,
            self.$leafThicknessScaleOne,
            self.$leafThicknessScaleZero,
            self.$bevelThickness,
            self.$bevelScaleZero,
            self.$bevelScaleOne,
          ],
        (
          $leafCenterOffsetY,
          $leafCenterOffsetZ,
          $leafCenterRotationY,
          $leafLengthShapekeyValue,
          $leafLengthScaleZero,
          $leafLengthScaleOne,
          $leafWidthShapekeyValue,
          $leafWidthScaleZero,
          $leafWidthScaleOne,
          $leafThicknessShapekeyValue,
          $leafThicknessScaleOne,
          $leafThicknessScaleZero,
          $bevelThickness,
          $bevelScaleZero,
          $bevelScaleOne
        ) => ({
          offsetY: $leafCenterOffsetY,
          offsetZ: $leafCenterOffsetZ,
          rotationY: $leafCenterRotationY,
          morphTargetInfluences: {
            length: {
              value: $leafLengthShapekeyValue,
              scaleZero: $leafLengthScaleZero,
              scaleOne: $leafLengthScaleOne,
            },
            width: {
              value: $leafWidthShapekeyValue,
              scaleZero: $leafWidthScaleZero,
              scaleOne: $leafWidthScaleOne,
            },
            thickness: {
              value: $leafThicknessShapekeyValue,
              scaleOne: $leafThicknessScaleOne,
              scaleZero: $leafThicknessScaleZero,
            },
            bevel: {
              value: $bevelThickness,
              scaleZero: $bevelScaleZero,
              scaleOne: $bevelScaleOne,
            },
          },
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectPropertiesComponent, 'leafCenter2', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafCenterOffsetY,
            self.$leafCenter2OffsetZ,
            self.$leafCenter2RotationY,
            self.$leafLengthShapekeyValue,
            self.$leafLengthScaleZero,
            self.$leafLengthScaleOne,
            self.$leafWidthShapekeyValue,
            self.$leafWidthScaleZero,
            self.$leafWidthScaleOne,
            self.$leafThicknessShapekeyValue,
            self.$leafThicknessScaleOne,
            self.$leafThicknessScaleZero,
            self.$bevelThickness,
            self.$bevelScaleZero,
            self.$bevelScaleOne,
          ],
        (
          $leafCenterOffsetY,
          $leafCenter2OffsetZ,
          $leafCenter2RotationY,
          $leafLengthShapekeyValue,
          $leafLengthScaleZero,
          $leafLengthScaleOne,
          $leafWidthShapekeyValue,
          $leafWidthScaleZero,
          $leafWidthScaleOne,
          $leafThicknessShapekeyValue,
          $leafThicknessScaleOne,
          $leafThicknessScaleZero,
          $bevelThickness,
          $bevelScaleZero,
          $bevelScaleOne
        ) => ({
          offsetY: $leafCenterOffsetY,
          offsetZ: $leafCenter2OffsetZ,
          rotationY: $leafCenter2RotationY,
          morphTargetInfluences: {
            length: {
              value: $leafLengthShapekeyValue,
              scaleZero: $leafLengthScaleZero,
              scaleOne: $leafLengthScaleOne,
            },
            width: {
              value: $leafWidthShapekeyValue,
              scaleZero: $leafWidthScaleZero,
              scaleOne: $leafWidthScaleOne,
            },
            thickness: {
              value: $leafThicknessShapekeyValue,
              scaleOne: $leafThicknessScaleOne,
              scaleZero: $leafThicknessScaleZero,
            },
            bevel: {
              value: $bevelThickness,
              scaleZero: $bevelScaleZero,
              scaleOne: $bevelScaleOne,
            },
          },
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectPropertiesComponent, 'leafPerimeter', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafPerimeterOffsetY,
            self.$leafPerimeterInnerDiameter,
            self.$leafPerimeterInnerDiameterScaleZero,
            self.$leafPerimeterInnerDiameterScaleOne,
            self.$leafPerimeterOuterDiameter,
            self.$leafPerimeterOuterDiameterScaleZero,
            self.$leafPerimeterOuterDiameterScaleOne,
            self.$leafPerimeterThickness,
            self.$leafPerimeterThicknessScaleOne,
            self.$leafPerimeterThicknessScaleZero,
          ],
        (
          $leafPerimeterOffsetY,
          $leafPerimeterInnerDiameter,
          $leafPerimeterInnerDiameterScaleZero,
          $leafPerimeterInnerDiameterScaleOne,
          $leafPerimeterOuterDiameter,
          $leafPerimeterOuterDiameterScaleZero,
          $leafPerimeterOuterDiameterScaleOne,
          $leafPerimeterThickness,
          $leafPerimeterThicknessScaleOne,
          $leafPerimeterThicknessScaleZero
        ) => ({
          offsetY: $leafPerimeterOffsetY,
          morphTargetInfluences: {
            innerDiameter: {
              value: $leafPerimeterInnerDiameter,
              scaleZero: $leafPerimeterInnerDiameterScaleZero,
              scaleOne: $leafPerimeterInnerDiameterScaleOne,
            },
            outerDiameter: {
              value: $leafPerimeterOuterDiameter,
              scaleZero: $leafPerimeterOuterDiameterScaleZero,
              scaleOne: $leafPerimeterOuterDiameterScaleOne,
            },
            thickness: {
              value: $leafPerimeterThickness,
              scaleOne: $leafPerimeterThicknessScaleOne,
              scaleZero: $leafPerimeterThicknessScaleZero,
            },
          },
        })
      )
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'thicknessType', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.thicknessType, self.$thicknessTypeOptions],
        (thicknessType, $thicknessTypeOptions) => ({
          value: thicknessType,
          options: $thicknessTypeOptions,
        })
      ),
      state => {
        ThicknessesTypes.assert(state.value)
        return { thicknessType: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'edgeStyle', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.edgeStyle],
        edgeStyle => ({
          value: edgeStyle,
          options: [
            {
              label: 'Clean',
              value: 'Clean',
              img: '/images/edge/edge-clean.jpeg',
            },
            {
              label: 'Natural Wavy',
              value: 'NaturalWavy',
              img: '/images/edge/edge-natural-wavy.jpg',
            },
            {
              label: 'Tumbled',
              value: 'Tumbled',
              img: '/images/edge/edge-tumbled.jpg',
            },
            {
              label: 'Dupont',
              value: 'Dupont',
              img: '/images/edge/edge-ogee.jpg',
            },
            {
              label: 'Beveled',
              value: 'Beveled',
              img: '/images/edge/edge-beveled.jpg',
            },
          ],
        })
      ),
      state => {
        EdgeStyles.assert(state.value)
        return { edgeStyle: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'distressLevel', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.distressLevel],
        distressLevel => ({
          value: distressLevel,
          options: [
            {
              label: 'Clean',
              value: 'Clean',
              img: '/images/distress/distress-clean.jpeg',
            },
            {
              label: 'Minimal',
              value: 'Minimal',
              img: '/images/distress/distress-minimal.jpeg',
            },
            {
              label: 'Light',
              value: 'Light',
              img: '/images/distress/distress-light.jpeg',
            },
            {
              label: 'Medium',
              value: 'Medium',
              img: '/images/distress/distress-medium.jpeg',
            },
            {
              label: 'Heavy',
              value: 'Heavy',
              img: '/images/distress/distress-heavy.jpeg',
            },
            {
              label: 'Rough Sawn',
              value: 'RoughSawn',
              img: '/images/distress/distress-rough-sawn.jpeg',
            },
          ],
        })
      ),
      state => {
        DistressLevels.assert(state.value)
        return { distressLevel: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'leafType', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.leafType, self.$leafTypeOptions],
        (leafType, $leafTypeOptions) => ({
          value: leafType,
          options: $leafTypeOptions,
        })
      ),
      state => {
        LeafTypes.assert(state.value)
        return { leafType: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'leafAmount', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.leafAmount, self.$leafAmountOptions],
        (leafAmount, $leafAmountOptions) => ({
          value: leafAmount,
          options: $leafAmountOptions,
        })
      ),
      state => {
        assertNumber(state.value)
        return { leafAmount: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'withGrooves', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.withGrooves],
        withGrooves => ({
          value: withGrooves,
          options: [
            {
              label: 'No Grooves',
              value: false,
            },
            {
              label: 'Grooves',
              value: true,
            },
          ],
        })
      ),
      state => {
        assertBoolean(state.value)
        return { withGrooves: state.value }
      }
    ),

  attachComponent
    .withComponent(attributeSelectionComponent, 'withCornerRadius', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.withCornerRadius],
        withCornerRadius => ({
          value: withCornerRadius,
          options: [
            {
              label: 'No Rounded Corners',
              value: false,
            },
            {
              label: 'Rounded Corners',
              value: true,
            },
          ],
        })
      ),
      state => {
        assertBoolean(state.value)
        return { withCornerRadius: state.value }
      }
    ),

  attachComponent
    .withComponent(materialSwatchComponent, 'finish', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.finish],
        finish => ({
          selectedSwatchKey: finish,
          swatches: {
            BlackTone: {
              color: '#111111',
              swatchName: 'Black Tone',
            },
            EbonyTone: {
              color: '#2C2C2C',
              swatchName: 'Ebony Tone',
            },
            Gray: {
              color: '#808080',
              swatchName: 'Gray',
            },
            DarkBrownTone: {
              color: '#4B3621',
              swatchName: 'Dark Brown Tone',
            },
            MediumBrownTone: {
              color: '#826344',
              swatchName: 'Medium Brown Tone',
            },
            LightBrownTone: {
              color: '#B3865E',
              swatchName: 'Light Brown Tone',
            },
            NaturalTone: {
              color: '#D2B48C',
              swatchName: 'Natural Tone',
            },
            Earthtone: {
              color: '#8B5E3C',
              swatchName: 'Earthtone',
            },
            Taupe: {
              color: '#483C32',
              swatchName: 'Taupe',
            },
            Custom: {
              color: '#FFFFFF',
              swatchName: 'Custom',
            },
          },
        })
      ),
      state => {
        Finishes.assert(state.selectedSwatchKey)
        return { finish: state.selectedSwatchKey }
      }
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'top', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$repeatX,
            self.$repeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $repeatX,
          $repeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $repeatX,
          repeatY: $repeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'leafLeft', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$leafRepeatX,
            self.$leafRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $leafRepeatX,
          $leafRepeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $leafRepeatX,
          repeatY: $leafRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'leafRight', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$leafRepeatX,
            self.$leafRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $leafRepeatX,
          $leafRepeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $leafRepeatX,
          repeatY: $leafRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'leafCenter', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$leafRepeatX,
            self.$leafRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $leafRepeatX,
          $leafRepeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $leafRepeatX,
          repeatY: $leafRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'leafCenter2', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$leafRepeatX,
            self.$leafRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $leafRepeatX,
          $leafRepeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $leafRepeatX,
          repeatY: $leafRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityObjectRemoteMaterialOverrideComponent, 'leafPerimeter', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$textureOffsetX,
            self.$normalMapUrl,
            self.$distressBumpMapUrl,
            self.$distressBumpScale,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$materialNormalScale,
            self.$materialReflectivity,
            self.$leafPerimeterRepeatX,
            self.$leafPerimeterRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $textureOffsetX,
          $normalMapUrl,
          $distressBumpMapUrl,
          $distressBumpScale,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $materialNormalScale,
          $materialReflectivity,
          $leafPerimeterRepeatX,
          $leafPerimeterRepeatY
        ) => ({
          materialsToOverride: ['TableTopMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          offsetX: $textureOffsetX,
          normalMapUrl: $normalMapUrl,
          bumpMapUrl: $distressBumpMapUrl,
          bumpScale: $distressBumpScale,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $materialNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $leafPerimeterRepeatX,
          repeatY: $leafPerimeterRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(sceneEntityRemoteMaterialOverrideComponent, 'topAndLeafEdge', true)
    .withAlias('Top')
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$fileUrl,
            self.$finishColor,
            self.$textureUrl,
            self.$edgeNormalMapUrl,
            self.$roughnessMapUrl,
            self.$aoMapUrl,
            self.$materialRoughness,
            self.$materialMetalness,
            self.$materialEnvMapIntensity,
            self.$materialSpecularIntensity,
            self.$edgeNormalScale,
            self.$materialReflectivity,
            self.$edgeRepeatX,
            self.$edgeRepeatY,
          ],
        (
          $fileUrl,
          $finishColor,
          $textureUrl,
          $edgeNormalMapUrl,
          $roughnessMapUrl,
          $aoMapUrl,
          $materialRoughness,
          $materialMetalness,
          $materialEnvMapIntensity,
          $materialSpecularIntensity,
          $edgeNormalScale,
          $materialReflectivity,
          $edgeRepeatX,
          $edgeRepeatY
        ) => ({
          materialsToOverride: ['TableTopEdgeMaterial'],
          fileUrl: $fileUrl,
          materialName: 'TableTopMaterial',
          color: $finishColor,
          textureUrl: $textureUrl,
          normalMapUrl: $edgeNormalMapUrl,
          roughnessMapUrl: $roughnessMapUrl,
          aoMapUrl: $aoMapUrl,
          roughness: $materialRoughness,
          metalness: $materialMetalness,
          envMapIntensity: $materialEnvMapIntensity,
          specularIntensity: $materialSpecularIntensity,
          normalScale: $edgeNormalScale,
          reflectivity: $materialReflectivity,
          repeatX: $edgeRepeatX,
          repeatY: $edgeRepeatY,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'topDimensionIndicatorX', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$topIndicatorXVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$topIndicatorXStartX, self.$topIndicatorXFixedY, self.$topIndicatorXFixedZ, self.$topIndicatorXEndX],
        ($topIndicatorXStartX, $topIndicatorXFixedY, $topIndicatorXFixedZ, $topIndicatorXEndX) => ({
          startX: $topIndicatorXStartX,
          startY: $topIndicatorXFixedY,
          startZ: $topIndicatorXFixedZ,
          endX: $topIndicatorXEndX,
          endY: $topIndicatorXFixedY,
          endZ: $topIndicatorXFixedZ,
          extrusionX: 0,
          extrusionY: 0,
          extrusionZ: -12,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'topDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$topDimensionIndicatorZVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [self.$topIndicatorZFixedX, self.$topIndicatorZFixedY, self.$topIndicatorZStartZ, self.$topIndicatorZEndZ],
        ($topIndicatorZFixedX, $topIndicatorZFixedY, $topIndicatorZStartZ, $topIndicatorZEndZ) => ({
          startX: $topIndicatorZFixedX,
          startY: $topIndicatorZFixedY,
          startZ: $topIndicatorZStartZ,
          endX: $topIndicatorZFixedX,
          endY: $topIndicatorZFixedY,
          endZ: $topIndicatorZEndZ,
          extrusionX: -12,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'shapeDimensionIndicatorX', true)
    .withAlias('Top')
    .withGradualFilter({ navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'] })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$shapeIndicatorXStartX,
            self.$shapeIndicatorXFixedY,
            self.$shapeIndicatorXFixedZ,
            self.$shapeIndicatorXEndX,
            self.$shapeDimensionIndicatorXExtrusionZ,
          ],
        (
          $shapeIndicatorXStartX,
          $shapeIndicatorXFixedY,
          $shapeIndicatorXFixedZ,
          $shapeIndicatorXEndX,
          $shapeDimensionIndicatorXExtrusionZ
        ) => ({
          startX: $shapeIndicatorXStartX,
          startY: $shapeIndicatorXFixedY,
          startZ: $shapeIndicatorXFixedZ,
          endX: $shapeIndicatorXEndX,
          endY: $shapeIndicatorXFixedY,
          endZ: $shapeIndicatorXFixedZ,
          extrusionX: 0,
          extrusionY: 0,
          extrusionZ: $shapeDimensionIndicatorXExtrusionZ,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'shapeDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$shapeDimensionIndicatorZVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$shapeIndicatorZFixedX,
            self.$shapeIndicatorZFixedY,
            self.$shapeIndicatorZStartZ,
            self.$shapeIndicatorZEndZ,
            self.$shapeDimensionIndicatorZExtrusionX,
          ],
        (
          $shapeIndicatorZFixedX,
          $shapeIndicatorZFixedY,
          $shapeIndicatorZStartZ,
          $shapeIndicatorZEndZ,
          $shapeDimensionIndicatorZExtrusionX
        ) => ({
          startX: $shapeIndicatorZFixedX,
          startY: $shapeIndicatorZFixedY,
          startZ: $shapeIndicatorZStartZ,
          endX: $shapeIndicatorZFixedX,
          endY: $shapeIndicatorZFixedY,
          endZ: $shapeIndicatorZEndZ,
          extrusionX: $shapeDimensionIndicatorZExtrusionX,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'splitTableLeftDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$splitTableIndicatorZVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$splitTableLeftIndicatorZFixedX,
            self.$splitTableLeftIndicatorZFixedY,
            self.$splitTableLeftIndicatorZStartZ,
            self.$splitTableLeftIndicatorZEndZ,
            self.$splitTableLeftDimensionIndicatorZExtrusionX,
          ],
        (
          $splitTableLeftIndicatorZFixedX,
          $splitTableLeftIndicatorZFixedY,
          $splitTableLeftIndicatorZStartZ,
          $splitTableLeftIndicatorZEndZ,
          $splitTableLeftDimensionIndicatorZExtrusionX
        ) => ({
          startX: $splitTableLeftIndicatorZFixedX,
          startY: $splitTableLeftIndicatorZFixedY,
          startZ: $splitTableLeftIndicatorZStartZ,
          endX: $splitTableLeftIndicatorZFixedX,
          endY: $splitTableLeftIndicatorZFixedY,
          endZ: $splitTableLeftIndicatorZEndZ,
          extrusionX: $splitTableLeftDimensionIndicatorZExtrusionX,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'splitTableRightDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$splitTableIndicatorZVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$splitTableRightIndicatorZFixedX,
            self.$splitTableRightIndicatorZFixedY,
            self.$splitTableRightIndicatorZStartZ,
            self.$splitTableRightIndicatorZEndZ,
            self.$splitTableRightDimensionIndicatorZExtrusionX,
          ],
        (
          $splitTableRightIndicatorZFixedX,
          $splitTableRightIndicatorZFixedY,
          $splitTableRightIndicatorZStartZ,
          $splitTableRightIndicatorZEndZ,
          $splitTableRightDimensionIndicatorZExtrusionX
        ) => ({
          startX: $splitTableRightIndicatorZFixedX,
          startY: $splitTableRightIndicatorZFixedY,
          startZ: $splitTableRightIndicatorZStartZ,
          endX: $splitTableRightIndicatorZFixedX,
          endY: $splitTableRightIndicatorZFixedY,
          endZ: $splitTableRightIndicatorZEndZ,
          extrusionX: $splitTableRightDimensionIndicatorZExtrusionX,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'leafLeftDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$leafLeftVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafLeftIndicatorZFixedX,
            self.$leafLeftIndicatorZFixedY,
            self.$leafLeftIndicatorZStartZ,
            self.$leafLeftIndicatorZEndZ,
          ],
        ($leafLeftIndicatorZFixedX, $leafLeftIndicatorZFixedY, $leafLeftIndicatorZStartZ, $leafLeftIndicatorZEndZ) => ({
          startX: $leafLeftIndicatorZFixedX,
          startY: $leafLeftIndicatorZFixedY,
          startZ: $leafLeftIndicatorZStartZ,
          endX: $leafLeftIndicatorZFixedX,
          endY: $leafLeftIndicatorZFixedY,
          endZ: $leafLeftIndicatorZEndZ,
          extrusionX: -4,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'leafRightDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$leafRightVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafRightIndicatorZFixedX,
            self.$leafRightIndicatorZFixedY,
            self.$leafRightIndicatorZStartZ,
            self.$leafRightIndicatorZEndZ,
          ],
        (
          $leafRightIndicatorZFixedX,
          $leafRightIndicatorZFixedY,
          $leafRightIndicatorZStartZ,
          $leafRightIndicatorZEndZ
        ) => ({
          startX: $leafRightIndicatorZFixedX,
          startY: $leafRightIndicatorZFixedY,
          startZ: $leafRightIndicatorZStartZ,
          endX: $leafRightIndicatorZFixedX,
          endY: $leafRightIndicatorZFixedY,
          endZ: $leafRightIndicatorZEndZ,
          extrusionX: -4,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'leafCenterDimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$leafCenterVisible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafCenterIndicatorZFixedX,
            self.$leafCenterIndicatorZFixedY,
            self.$leafCenterIndicatorZStartZ,
            self.$leafCenterIndicatorZEndZ,
          ],
        (
          $leafCenterIndicatorZFixedX,
          $leafCenterIndicatorZFixedY,
          $leafCenterIndicatorZStartZ,
          $leafCenterIndicatorZEndZ
        ) => ({
          startX: $leafCenterIndicatorZFixedX,
          startY: $leafCenterIndicatorZFixedY,
          startZ: $leafCenterIndicatorZStartZ,
          endX: $leafCenterIndicatorZFixedX,
          endY: $leafCenterIndicatorZFixedY,
          endZ: $leafCenterIndicatorZEndZ,
          extrusionX: -4,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),

  attachComponent
    .withComponent(dimensionIndicatorComponent, 'leafCenter2DimensionIndicatorZ', true)
    .withAlias('Top')
    .withGradualFilter({
      navigationStates: ['Shape', 'Base', 'Wood', 'Appearance', 'Accessories', 'Summary'],
      active: self => self.$leafCenter2Visible,
    })
    .attach(
      memoEval(
        // @ts-ignore
        self =>
          // keep deps on a separate line so @ts-ignore only covers the parameter
          [
            self.$leafCenter2IndicatorZFixedX,
            self.$leafCenter2IndicatorZFixedY,
            self.$leafCenter2IndicatorZStartZ,
            self.$leafCenter2IndicatorZEndZ,
          ],
        (
          $leafCenter2IndicatorZFixedX,
          $leafCenter2IndicatorZFixedY,
          $leafCenter2IndicatorZStartZ,
          $leafCenter2IndicatorZEndZ
        ) => ({
          startX: $leafCenter2IndicatorZFixedX,
          startY: $leafCenter2IndicatorZFixedY,
          startZ: $leafCenter2IndicatorZStartZ,
          endX: $leafCenter2IndicatorZFixedX,
          endY: $leafCenter2IndicatorZFixedY,
          endZ: $leafCenter2IndicatorZEndZ,
          extrusionX: -4,
          extrusionY: 0,
          extrusionZ: 0,
          extrusionLineDashed: true,
        })
      )
    ),
]
